import React, {useEffect, useMemo, useState} from 'react'
import {ManageMemberGrid, Member} from './components/ManageMemberGrid'
import Pagination from '../components/Pagination'
import {ManageMemberStats} from './components/ManageMemberStats'
import {ManageMemberSearch} from './components/ManageMemberSearch'
import {ManageMemberTabNavigation} from './components/ManageMemberTabNavigation'
import {useSearchableManageMembers} from '../../hooks/manageMembers/useSearchableManageMembers'
import {ApiMember} from './models/ManageMemberModel'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { defaultProfileImage } from '../../utils/constants'

export const ManageMemberPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('members')
  const [searchQuery, setSearchQuery] = useState('')
  // Admin filters (WP-C): plan + active/inactive status (server-side via nobilis-social PR #39).
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10
  const offset = (page - 1) * limit

  const filters = useMemo(() => ({plan: planFilter, isActive: statusFilter}), [planFilter, statusFilter])
  const {data, refetch} = useSearchableManageMembers(searchQuery, activeTab, limit, offset, filters)

  // Reset to the first page whenever a filter/search/tab narrows the set.
  useEffect(() => {
    setPage(1)
  }, [searchQuery, activeTab, planFilter, statusFilter])

  // Accumulate plan titles seen across fetches so selecting a plan (which collapses the
  // filtered plansBreakdown to that one plan) doesn't empty the dropdown.
  const [planOptions, setPlanOptions] = useState<string[]>([])
  useEffect(() => {
    const titles = (data?.stats?.plansBreakdown ?? [])
      .map((p) => p.profile_CurrentSubscription_Plan_Title)
      .filter((t): t is string => Boolean(t))
    if (titles.length) {
      setPlanOptions((prev) => Array.from(new Set([...prev, ...titles])).sort())
    }
  }, [data])

  const tabs: any[] = [
    {id: 'members', label: 'Members'},
    {id: 'new-members', label: 'New Members'},
    {id: 'create-profile', label: 'Create Profile', badge: 2},
  ]

  const transformMember = (apiMember: ApiMember): Member => ({
    id: String(apiMember.id),
    name: `${apiMember.firstName} ${apiMember.lastName}`.trim(),
    avatar: apiMember.profilePicture,
    email: apiMember.email,
    memberSince: apiMember.memberSince,
    plan: apiMember.planName,
    assignedTo: apiMember.assignedTo?.name || 'Unassigned',
    assignedToAvatar: apiMember.assignedTo?.profilePicture || defaultProfileImage,
    isActive: apiMember.isActive,
  })

  const members = data?.results?.map(transformMember) || []

  const electiCount =
    data?.stats.plansBreakdown.find((p) =>
      p.profile_CurrentSubscription_Plan_Title?.toLowerCase().includes('electi')
    )?.count || 0

  const totalPages = data?.count ? Math.ceil(data.count / limit) : 1

  return (
    <div className='manage-member-page'>
      <h1 className='manage-member-page__title'>Manage Members</h1>

      <div className='manage-member-page__content'>
        <div className='manage-member-page__tabs' style={{width: '100%'}}>
          <ManageMemberTabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onCreateRecord={() => console.log('clicked')}
          />
        </div>

        <div className='manage-member-page__search'>
          <ManageMemberSearch value={searchQuery} onChange={setSearchQuery} />
        </div>

        <div className='manage-member-page__filters d-flex flex-wrap align-items-center gap-3 mb-4'>
          <select
            className='form-select w-auto'
            aria-label='Filter by plan'
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            disabled={planOptions.length === 0}
          >
            <option value=''>All plans</option>
            {planOptions.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>

          <select
            className='form-select w-auto'
            aria-label='Filter by status'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value=''>All statuses</option>
            <option value='active'>Active</option>
            <option value='inactive'>Inactive</option>
          </select>

          {(planFilter || statusFilter) && (
            <button
              type='button'
              className='nb-link-underline-500'
              onClick={() => {
                setPlanFilter('')
                setStatusFilter('')
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {data?.stats && (
          <div className='manage-member-page__stats'>
            <ManageMemberStats
              totalMembers={data.stats.totalMembers}
              activeMembers={data.stats.activeMembers}
              electiMembers={electiCount}
              newMembers={data.stats.newMembers}
            />
          </div>
        )}

        <div className='manage-member-page__table'>
          <ManageMemberGrid members={members} page={page} limit={limit} refresh={refetch} />
        </div>

        <div className='manage-member-page__pagination'>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>
    </div>
  )
}
